import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, Video, X, Star, GripVertical, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const ACCEPT_IMG = "image/jpeg,image/png,image/webp";
const ACCEPT_VID = "video/mp4,video/quicktime,video/webm";
const MAX_VID_SEC = 60;

// Extract media id from a /api/media/santralemlak/properties/{user}/{id}.{ext} URL
const idFromUrl = (u) => {
  const m = /\/([a-f0-9]{16,})\.[a-z0-9]+(?:\?|$)/i.exec(u || "");
  return m ? m[1] : null;
};
const softDelete = async (u) => {
  const id = idFromUrl(u);
  if (!id) return;
  try { await api.delete(`/admin/uploads/${id}`); } catch { /* silent */ }
};

/**
 * MediaUploader
 * props:
 *   images: string[]   video: string   onChange({images, video})
 */
export default function MediaUploader({ images = [], video = "", onChange }) {
  const [progress, setProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const imgInputRef = useRef(null);
  const vidInputRef = useRef(null);

  // Live mirror of current state so async completions see the latest values,
  // not the stale render-time snapshot (fixes iter-10 batch-upload state loss).
  const stateRef = useRef({ images, video });
  stateRef.current = { images, video };

  const patch = (fn) => {
    const next = fn(stateRef.current);
    stateRef.current = next;
    onChange(next);
  };

  const uploadImages = async (files) => {
    const list = Array.from(files).filter((f) => ACCEPT_IMG.split(",").includes(f.type));
    if (!list.length) { toast.error("Yalnızca JPG, PNG veya WebP yüklenebilir"); return; }
    let uploaded = 0;
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      const fd = new FormData();
      fd.append("file", f);
      setProgress({ kind: "image", pct: 0, idx: i + 1, total: list.length });
      try {
        const { data } = await api.post("/admin/uploads/image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => e.total && setProgress({ kind: "image", pct: Math.round((e.loaded / e.total) * 100), idx: i + 1, total: list.length }),
        });
        patch((s) => ({ ...s, images: [...s.images, data.url] }));
        uploaded += 1;
      } catch (e) {
        toast.error(e?.response?.data?.detail || `${f.name}: yüklenemedi`);
      }
    }
    setProgress(null);
    if (uploaded > 0) toast.success(`${uploaded} fotoğraf yüklendi`);
  };

  const uploadVideo = async (file) => {
    if (!file) return;
    if (!ACCEPT_VID.split(",").includes(file.type)) { toast.error("Yalnızca MP4, MOV veya WebM"); return; }
    const dur = await new Promise((res) => {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => res(v.duration || 0);
      v.onerror = () => res(0);
      v.src = URL.createObjectURL(file);
    });
    if (dur > MAX_VID_SEC + 0.5) {
      toast.error("Video süresi 1 dakikadan uzun olamaz. Lütfen 60 saniye veya daha kısa bir video seçin.");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setProgress({ kind: "video", pct: 0 });
    try {
      // Delete any previously attached video before overwriting
      const prev = video;
      const { data } = await api.post("/admin/uploads/video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && setProgress({ kind: "video", pct: Math.round((e.loaded / e.total) * 100) }),
      });
      patch((s) => ({ ...s, video: data.url }));
      if (prev) softDelete(prev);
      toast.success("Video yüklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Video yüklenemedi");
    } finally { setProgress(null); }
  };

  const onDrop = useCallback(async (e) => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    const imgs = files.filter((f) => f.type.startsWith("image/"));
    const vid = files.find((f) => f.type.startsWith("video/"));
    if (imgs.length) await uploadImages(imgs);
    if (vid) await uploadVideo(vid);
    // eslint-disable-next-line
  }, [images, video]);

  const removeImage = (idx) => {
    const target = images[idx];
    patch((s) => ({ ...s, images: s.images.filter((_, i) => i !== idx) }));
    softDelete(target);
  };
  const makeCover = (idx) => {
    if (idx === 0) return;
    patch((s) => {
      const next = [...s.images];
      const [pick] = next.splice(idx, 1);
      next.unshift(pick);
      return { ...s, images: next };
    });
  };
  const move = (idx, dir) => {
    patch((s) => {
      const j = idx + dir;
      if (j < 0 || j >= s.images.length) return s;
      const next = [...s.images];
      [next[idx], next[j]] = [next[j], next[idx]];
      return { ...s, images: next };
    });
  };
  const removeVideo = () => {
    const prev = video;
    patch((s) => ({ ...s, video: "" }));
    softDelete(prev);
  };

  const absUrl = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

  return (
    <div className="space-y-4" data-testid="media-uploader">
      {/* Drop / picker area */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${dragOver ? "border-[#C0562F] bg-orange-50" : "border-slate-300 bg-slate-50"}`}
      >
        <Upload className="w-6 h-6 text-slate-500 mx-auto mb-2" />
        <div className="font-display text-lg text-slate-900 mb-1">Fotoğraf veya Video Sürükleyip Bırakın</div>
        <div className="text-xs text-slate-500 mb-4">Desteklenen fotoğraf formatları: JPG, PNG, WebP · Maksimum video süresi: 60 saniye</div>
        <div className="flex flex-wrap gap-3 justify-center">
          <button type="button" data-testid="upload-photo-btn" onClick={() => imgInputRef.current?.click()} className="btn-accent px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Fotoğraf Yükle</button>
          <button type="button" data-testid="upload-video-btn" onClick={() => vidInputRef.current?.click()} className="btn-primary px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2"><Video className="w-4 h-4" /> Video Yükle</button>
        </div>
        <input ref={imgInputRef} type="file" accept={ACCEPT_IMG} multiple hidden data-testid="photo-input" onChange={(e) => e.target.files && uploadImages(e.target.files)} />
        <input ref={vidInputRef} type="file" accept={ACCEPT_VID} hidden data-testid="video-input" onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])} />

        {progress && (
          <div className="mt-4 max-w-md mx-auto" data-testid="upload-progress">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
              <span className="inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {progress.kind === "image" ? `Fotoğraf yükleniyor (${progress.idx}/${progress.total})` : "Video yükleniyor"}</span>
              <span className="font-semibold">%{progress.pct}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-[#C0562F] transition-all" style={{ width: `${progress.pct}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* Photos grid */}
      {images.length > 0 && (
        <div>
          <div className="label-caps mb-3">Fotoğraflar ({images.length}) — ilk fotoğraf kapak olarak kullanılır</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((src, i) => (
              <div key={i} data-testid={`media-thumb-${i}`} className={`relative group aspect-[4/3] rounded-lg overflow-hidden border-2 ${i === 0 ? "border-[#C0562F]" : "border-slate-200"}`}>
                <img src={absUrl(src)} alt="" className="w-full h-full object-cover" />
                {i === 0 && (
                  <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded bg-[#C0562F] text-white text-[9px] font-bold uppercase tracking-widest">Kapak</span>
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  {i !== 0 && <button type="button" title="Kapak yap" onClick={() => makeCover(i)} className="w-7 h-7 rounded bg-white/90 hover:bg-white flex items-center justify-center" data-testid={`make-cover-${i}`}><Star className="w-3.5 h-3.5 text-[#C0562F]" /></button>}
                  <button type="button" title="Sola" onClick={() => move(i, -1)} className="w-7 h-7 rounded bg-white/90 hover:bg-white flex items-center justify-center"><GripVertical className="w-3.5 h-3.5 text-slate-700" /></button>
                  <button type="button" title="Sil" onClick={() => removeImage(i)} className="w-7 h-7 rounded bg-red-500 hover:bg-red-600 flex items-center justify-center" data-testid={`remove-image-${i}`}><X className="w-3.5 h-3.5 text-white" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video preview */}
      {video && (
        <div>
          <div className="label-caps mb-3">Video (Maks 60 sn)</div>
          <div className="relative rounded-lg overflow-hidden bg-slate-900 max-w-md">
            <video src={absUrl(video)} controls className="w-full aspect-video" data-testid="video-preview" />
            <button type="button" onClick={removeVideo} data-testid="remove-video" className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center"><X className="w-4 h-4 text-white" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
